import { type UnknownAction } from '@reduxjs/toolkit';

import {
    type Conversation,
    type ConversationFact,
    type ConversationMessage,
    type NewConversationBody,
} from 'api/userSupportAPI';
import { handleRTKQueryError } from 'services/Error';
import {
    mapConversation,
    mapMessage,
    type MappedConversation,
    type MappedMessage,
    type ReferralConversationCounts,
} from 'services/userSupportHelpers';
import { TagTypes, userSupportApi } from 'store/api/userSupportApi';
import { updateConversationMessages } from 'store/userSupport/userSupportSlice';

/**
 * Seconds a cached unread-count result is reused by a newly mounting component. The header and
 * a referral's messages both load counts on mount, usually within moments of each other.
 */
export const UNREAD_COUNTS_STALE_SECONDS = 30;

interface UnreadCountsResponse {
    total_unread: number;
    total_open: number;
    by_referral: Record<string, { unread: number; open: number }>;
}

export interface UnreadCounts {
    unreadConversationCount?: number;
    referralConversationCounts?: ReferralConversationCounts;
}

/**
 * Referral chat pane endpoints: the REST send (userSupportRestWrites) and on-demand reads
 * (userSupportRestFetch) that replace the WebSocket.
 */
const chatEndpoints = userSupportApi.injectEndpoints({
    endpoints: (build) => ({
        getConversationByDisplayId: build.query<MappedConversation, string>({
            query: (displayId) => ({ url: '/conversations', params: { display_id: displayId } }),
            transformResponse: mapConversation,
            transformErrorResponse: handleRTKQueryError('Unable to find conversation.'),
        }),
        searchReferralConversations: build.query<
            MappedConversation[],
            { referralId: string; searchTerm: string }
        >({
            query: ({ referralId, searchTerm }) => ({
                url: '/conversations/list',
                method: 'POST',
                params: { referral_id: referralId, search_term: searchTerm },
            }),
            providesTags: (results) =>
                (results ?? []).map(({ conversationId }) => ({
                    type: TagTypes.CONVERSATION,
                    id: conversationId,
                })),
            transformResponse: (response: Conversation[]) => response.map(mapConversation),
            transformErrorResponse: handleRTKQueryError('Unable to search conversations.'),
        }),
        getConversationFacts: build.query<ConversationFact[], string>({
            query: (conversationId) => `/conversations/${conversationId}/facts`,
            providesTags: (_results, _error, conversationId) => [
                { type: TagTypes.CONVERSATION_FACTS, id: conversationId },
            ],
            transformErrorResponse: handleRTKQueryError(
                'An error occurred while retrieving conversation facts.',
            ),
        }),
        // Callers surface write failures with their own toast, so no transformErrorResponse here.
        createConversation: build.mutation<
            MappedConversation,
            { referralId: string; body: NewConversationBody }
        >({
            query: ({ referralId, body }) => ({
                url: `/conversations/referral/${referralId}`,
                method: 'POST',
                body,
            }),
            transformResponse: mapConversation,
            // No UNREAD_COUNTS: the caller selects the new conversation, and that messages read
            // refreshes the counts (syncConversationRead)
            invalidatesTags: (_results, _error, { referralId }) => [
                { type: TagTypes.REFERRAL_CONVERSATIONS, id: referralId },
                TagTypes.PAGED_CONVERSATIONS,
            ],
        }),
        // Callers surface send failures with their own toast, so no transformErrorResponse here.
        sendMessage: build.mutation<
            MappedMessage,
            { conversationId: string; details: { content: { message: string } } }
        >({
            query: ({ conversationId, details }) => ({
                url: `/messages/${conversationId}`,
                method: 'POST',
                body: details,
            }),
            transformResponse: mapMessage,
            invalidatesTags: (_results, _error, { conversationId }) => [
                { type: TagTypes.CONVERSATION, id: conversationId },
                TagTypes.UNREAD_COUNTS,
            ],
        }),
        // Reading a conversation's messages marks it read server-side, so callers must refetch on
        // every select (refetchOnMountOrArgChange: true) rather than serve the cache.
        getMessages: build.query<MappedMessage[], string>({
            query: (conversationId) => `/messages/${conversationId}`,
            providesTags: (_results, _error, conversationId) => [
                { type: TagTypes.MESSAGES, id: conversationId },
            ],
            transformResponse: (response: ConversationMessage[]) => response.map(mapMessage),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred while retrieving messages.',
            ),
        }),
        getReferralConversations: build.query<MappedConversation[], string>({
            query: (referralId) => ({
                url: '/conversations/list',
                method: 'POST',
                params: { referral_id: referralId },
            }),
            providesTags: (results, _error, referralId) => [
                { type: TagTypes.REFERRAL_CONVERSATIONS, id: referralId },
                ...(results ?? []).map(({ conversationId }) => ({
                    type: TagTypes.CONVERSATION,
                    id: conversationId,
                })),
            ],
            transformResponse: (response: Conversation[]) => response.map(mapConversation),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred while retrieving conversations.',
            ),
        }),
        getUnreadCounts: build.query<UnreadCounts, void>({
            query: () => '/conversations/unread_counts',
            providesTags: [TagTypes.UNREAD_COUNTS],
            // Same shape the socket-fed slice exposes, so badge consumers read either source
            transformResponse: ({
                total_unread: totalUnread,
                by_referral: byReferral,
            }: UnreadCountsResponse) => ({
                unreadConversationCount: totalUnread,
                referralConversationCounts: Object.fromEntries(
                    Object.entries(byReferral ?? {}).map(([referralId, { open, unread }]) => [
                        referralId,
                        { open, unread, conversations: {} },
                    ]),
                ),
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred while retrieving unread counts.',
            ),
        }),
    }),
});

type ChatUtil = typeof chatEndpoints.util;
type ChatState = Parameters<ChatUtil['selectCachedArgsForQuery']>[0];

interface LifecycleApi<T> {
    dispatch: (action: UnknownAction | ReturnType<ChatUtil['updateQueryData']>) => unknown;
    queryFulfilled: Promise<{ data: T }>;
}

/**
 * Puts a sent message into both message stores: the socket-fed slice (dual-run, where a REST
 * send gets no socket push until the server's polling loop runs), and the getMessages query
 * cache used when reads go over REST. Writing it straight into the cache avoids refetching the
 * whole conversation after every send.
 */
export const syncSentMessage = async (
    { conversationId }: { conversationId: string },
    { dispatch, queryFulfilled }: LifecycleApi<MappedMessage>,
    apiUtil: Pick<ChatUtil, 'updateQueryData'>,
) => {
    let message: MappedMessage;
    try {
        ({ data: message } = await queryFulfilled);
    } catch {
        return;
    }
    dispatch(
        updateConversationMessages({
            conversationId,
            messages: { [message.messageId]: message },
        }),
    );
    dispatch(
        apiUtil.updateQueryData('getMessages', conversationId, (draft) => {
            if (!draft.some(({ messageId }) => messageId === message.messageId)) {
                draft.push(message);
            }
        }),
    );
};

/**
 * Fetching a conversation's messages marks it read. Once that lands, the cached conversation
 * lists drop its unread marker (refetching a list for one known field would cost a request) and
 * unread counts refetch; requesting them alongside the read can return the pre-read counts.
 */
export const syncConversationRead = async (
    conversationId: string,
    {
        dispatch,
        getState,
        queryFulfilled,
    }: LifecycleApi<MappedMessage[]> & { getState: () => ChatState },
    apiUtil: Pick<ChatUtil, 'invalidateTags' | 'selectCachedArgsForQuery' | 'updateQueryData'>,
) => {
    try {
        await queryFulfilled;
    } catch {
        return;
    }
    const markRead = (conversations: MappedConversation[]) => {
        const conversation = conversations.find((c) => c.conversationId === conversationId);
        if (conversation) {
            conversation.isUnread = false;
            conversation.subject.isUnread = false;
        }
    };
    apiUtil
        .selectCachedArgsForQuery(getState(), 'getReferralConversations')
        .forEach((referralId) =>
            dispatch(apiUtil.updateQueryData('getReferralConversations', referralId, markRead)),
        );
    apiUtil
        .selectCachedArgsForQuery(getState(), 'searchReferralConversations')
        .forEach((args) =>
            dispatch(apiUtil.updateQueryData('searchReferralConversations', args, markRead)),
        );
    dispatch(apiUtil.invalidateTags([TagTypes.UNREAD_COUNTS]));
};

export const userSupportChatApi = chatEndpoints.enhanceEndpoints({
    endpoints: {
        sendMessage: {
            onQueryStarted: (arg, lifecycle) => syncSentMessage(arg, lifecycle, chatEndpoints.util),
        },
        getMessages: {
            onQueryStarted: (arg, lifecycle) =>
                syncConversationRead(arg, lifecycle, chatEndpoints.util),
        },
    },
});

export const {
    useCreateConversationMutation,
    useGetConversationByDisplayIdQuery,
    useGetConversationFactsQuery,
    useGetMessagesQuery,
    useGetReferralConversationsQuery,
    useGetUnreadCountsQuery,
    useSearchReferralConversationsQuery,
    useSendMessageMutation,
} = userSupportChatApi;
